import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { TenantSettingsTemplateParamTypes } from './templateRoute';
import {
  Button,
  Checkbox,
  FormContainer,
  FormItem,
  Input,
  Layout,
  Modal,
  NativeSelect,
  TextArea,
} from 'elmo-elements';
import SettingsHeader from '@/pages/Settings/components/Header';
import { LayoutSectionComponent } from '@/components/LayoutSection';
import { useForm } from 'react-hook-form';
import {
  useGetTemplateQuery,
  useUpdateTemplateMutation,
} from '@/modules/services';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import SettingsBaseRoutes, {
  TenantSettingsTabs,
} from '@/pages/Settings/SettingRoutes';
import { useUserHasRole } from '@/lib/auth/useUserHasRoles';
import { Role } from '@/lib/auth/roles/role.enum';
import MyLibraryRoutes from '../MyLibrary/MyLibraryRoutes';
import config from '@/lib/config';
// Replaced CKFileUpload with a native file input + API upload
import { CKEditor } from 'ckeditor4-react';
import { useDispatch } from 'react-redux';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { TemplateFormDataType } from '@/modules/types/template';
import { useUploadTemplateImageMutation } from '@/modules/services/templates';
import './templateStyle.scss';

export enum TemplatePlayerType {
  Default = 1,
  Modern = 2,
}

const TemplatePlayerTypeSelection = [
  { key: 'Default', value: TemplatePlayerType.Default },
  { key: 'Modern', value: TemplatePlayerType.Modern },
];

export const defaultTemplateValue = {
  name: 'Default Template name',
  content: 'Default Template content',
  isFavourite: false,
  templateMetadata: {
    img: '',
    playerType: 2,
    description: '',
  },
};

const schema = yup.object().shape({
  name: yup.string().max(50).required('This field is required.'),
  content: yup.string().max(5000).required('This field is required'),
  isFavourite: yup.boolean().default(defaultTemplateValue.isFavourite),
  templateMetadata: yup.object().shape({
    img: yup.string().max(50),
    playerType: yup
      .number()
      .min(1)
      .max(2)
      .default(defaultTemplateValue.templateMetadata.playerType),
    description: yup.string().max(500),
  }),
});

const TemplateSetupComponent = (): ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { templateId } = useParams<TenantSettingsTemplateParamTypes>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    data: templateData,
    isLoading,
    isSuccess,
  } = useGetTemplateQuery(templateId);
  const [updateTemplate, updateTemplateStatus] = useUpdateTemplateMutation();
  const [uploadTemplateImage, uploadTemplateImageStatus] =
    useUploadTemplateImageMutation();

  const hasAdminRole = useUserHasRole([Role.Admin, Role.Super, Role.Migration]);

  const {
    watch,
    getValues,
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TemplateFormDataType>({
    resolver: yupResolver(schema),
    defaultValues: defaultTemplateValue,
  });

  const templateIsFavourite = watch('isFavourite');
  const templateDescription = watch('templateMetadata.description');
  const templatePlayerType = watch('templateMetadata.playerType');
  const templateContent = watch('content');
  const templateImage = watch('templateMetadata.img');
  const handleChange = (key: keyof TemplateFormDataType, value: any) => {
    setValue(key, value);
  };

  const handleImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setSelectedImageFile(null);
      return;
    }
    if (!file.type.startsWith('image/')) return;
    if (file.size > 10 * 1024 * 1024) return;
    setSelectedImageFile(file);
    if (!getValues('templateMetadata.img')) {
      setValue('templateMetadata.img', file.name);
    }
  };

  const uploadImageIfNeeded = (currentImageName: string): Promise<string> => {
    if (!selectedImageFile) {
      return Promise.resolve(currentImageName);
    }

    const desiredName = currentImageName || selectedImageFile.name;

    return uploadTemplateImage({
      file: selectedImageFile,
      name: desiredName,
    })
      .unwrap()
      .then((response) => response?.fileName || desiredName)
      .catch((error) => {
        const message = error?.data?.message || 'Upload template image failed.';
        dispatch({
          type: flashMessageActions.addFlashMessage,
          payload: {
            isActive: true,
            message,
          },
        });
        return '';
      });
  };

  const handleCancelEdit = () => {
    history.push(
      generatePath(SettingsBaseRoutes.path, {
        tabSelected: TenantSettingsTabs.Template,
      }),
    );
  };

  const headerTitle = 'Edit Template';
  const backButton = {
    title: headerTitle,
    onClick: () => {
      history.goBack();
    },
  };

  useEffect(() => {
    const hasTemplateData = !isLoading && templateData;

    if (!hasTemplateData) return;

    // Prepopulate form with templateData
    reset(templateData);
  }, [isLoading, templateData, reset]);

  useEffect(() => {
    // Redirect back to template list - templateId is not valid
    if (isSuccess && !templateData) {
      history.replace(
        generatePath(SettingsBaseRoutes.path, {
          tabSelected: TenantSettingsTabs.Template,
        }),
      );
    }
  }, [history, isSuccess, templateData]);

  useEffect(() => {
    if (!updateTemplateStatus.isError && !updateTemplateStatus.isSuccess)
      return;

    let message = 'Update template error! Something went wrong.';
    if (updateTemplateStatus.isSuccess) {
      message = 'Update template successful.';
    }

    dispatch({
      type: flashMessageActions.addFlashMessage,
      payload: {
        isActive: true,
        message,
      },
    });
  }, [updateTemplateStatus]);

  if (hasAdminRole !== undefined && !hasAdminRole) {
    history.replace(generatePath(MyLibraryRoutes.path));
  }

  if (isLoading || !templateData) {
    return (
      <>
        <Layout.Header>
          <SettingsHeader headerTitle={headerTitle} backButton={backButton} />
        </Layout.Header>
        <div className="editor-layout__body">
          <Layout.Content>
            <div />
          </Layout.Content>
        </div>
      </>
    );
  }

  return (
    <>
      <Modal
        id="TemplateModalDialog"
        type="medium"
        isOpened={isModalOpen}
        closeModal={() => {
          setIsModalOpen(false);
        }}
        primaryButton={
          <>
            <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="primary" onClick={() => handleCancelEdit()}>
              Continue
            </Button>
          </>
        }
        title="Cancel without saving"
      >
        All unsaved changes will be lost. Are you sure you want to leave this
        page?
      </Modal>
      <Layout.Header>
        <SettingsHeader headerTitle={headerTitle} backButton={backButton} />
      </Layout.Header>
      <div className="editor-layout__body">
        <Layout.Content>
          <form
            onSubmit={handleSubmit(async (submitData) => {
              const finalImageName = await uploadImageIfNeeded(
                submitData.templateMetadata?.img || '',
              );

              if (!finalImageName) {
                return;
              }

              await updateTemplate({
                ...templateData,
                ...submitData,
                templateMetadata: {
                  ...submitData.templateMetadata,
                  img: finalImageName,
                },
              });

              setSelectedImageFile(null);
            })}
          >
            <LayoutSectionComponent
              className="remove-overflow-hidden"
              isFirst={true}
              content={
                <>
                  <FormContainer maxColumns={2}>
                    <FormItem
                      label="Template Name"
                      message={errors.name && errors.name?.message}
                      status={errors.name ? 'error' : 'default'}
                      isBlock={true}
                    >
                      <Input
                        {...register('name', {})}
                        onChange={(e) =>
                          handleChange(
                            e.target.name as keyof TemplateFormDataType,
                            e.target.value,
                          )
                        }
                        name="name"
                        value={watch('name')}
                        ariaLabel="name"
                      />
                    </FormItem>
                  </FormContainer>

                  <FormContainer maxColumns={2}>
                    <FormItem
                      label="Description"
                      message={errors.templateMetadata?.description?.message}
                      status={
                        errors.templateMetadata?.description
                          ? 'error'
                          : 'default'
                      }
                      isBlock={true}
                    >
                      <TextArea
                        {...register('templateMetadata.description', {})}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleChange(
                            'templateMetadata.description' as keyof TemplateFormDataType,
                            e.target.value,
                          )
                        }
                        value={templateDescription}
                        ariaLabel="description"
                        id="elearning-description"
                      />
                    </FormItem>
                  </FormContainer>

                  <FormContainer maxColumns={2}>
                    <FormItem message="This template will be shown on creat slide pop up.">
                      <Checkbox
                        onChange={(e: any) => {
                          setValue('isFavourite', e.target.checked);
                        }}
                        isChecked={templateIsFavourite}
                        name="favourite template"
                        id="favourite template"
                        label="Favourite"
                        ariaLabel="favourite template checkbox"
                      />
                    </FormItem>
                  </FormContainer>

                  <FormContainer maxColumns={2}>
                    <FormItem
                      label="Layout Image"
                      message={
                        !selectedImageFile
                          ? 'No image file to upload selected.'
                          : errors.templateMetadata?.img?.message
                      }
                      status={
                        errors.templateMetadata?.img || !selectedImageFile
                          ? 'error'
                          : 'default'
                      }
                      size="sm"
                    >
                      <div className="template-image-upload">
                        <Input
                          {...register('templateMetadata.img', {})}
                          onChange={(e) => {
                            setValue('templateMetadata.img', e.target.value);
                          }}
                          value={templateImage || ''}
                          ariaLabel="Image file name"
                          placeholder="Enter image file name (e.g. banner.png)"
                        />
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageInputChange}
                          aria-label="Upload layout image"
                          style={{ display: 'none' }}
                        />
                        <Button
                          type="primary"
                          htmlType="button"
                          ariaLabel="Choose image file"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Choose File
                        </Button>
                      </div>
                    </FormItem>
                  </FormContainer>

                  <FormContainer maxColumns={2}>
                    <FormItem
                      label="Style"
                      size="sm"
                      message={errors.templateMetadata?.playerType?.message}
                      status={
                        errors.templateMetadata?.playerType
                          ? 'error'
                          : 'default'
                      }
                    >
                      <NativeSelect
                        value={watch('templateMetadata.playerType')}
                        onChange={(e) => {
                          handleChange(
                            'templateMetadata.playerType' as keyof TemplateFormDataType,
                            +e,
                          );
                        }}
                      >
                        {TemplatePlayerTypeSelection.map(
                          (playerTypeData, index) => (
                            <NativeSelect.Option
                              label={playerTypeData.key}
                              value={playerTypeData.value}
                              key={index}
                            />
                          ),
                        )}
                      </NativeSelect>
                    </FormItem>
                  </FormContainer>

                  <FormContainer maxColumns={2}>
                    <FormItem>
                      {templatePlayerType === 1 && (
                        <TextArea
                          {...register('content', {})}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange('content', e.target.value)
                          }
                          value={templateContent}
                          ariaLabel="content"
                        />
                      )}
                      {templatePlayerType === 2 && (
                        <CKEditor
                          editorUrl={config.ckEditorScriptUrl}
                          type="classic"
                          onChange={({ editor }) =>
                            handleChange('content', editor.getData())
                          }
                          name="content"
                          initData={templateContent}
                          onInstanceReady={(instance) => {
                            // Pre-populates CKeditor on reload
                            instance.editor.setData(getValues('content'));
                          }}
                          config={{
                            uiColor: '#FFFFFF',
                            width: '1024px',
                            height: '670px',
                          }}
                          style={{
                            borderRadius: '6px',
                            border: '1px solid rgba(0, 0, 0, 0.14)',
                            overflow: 'hidden',
                          }}
                        />
                      )}
                    </FormItem>
                  </FormContainer>
                </>
              }
            />

            <div className="mt-5">
              <Button
                type="primary"
                htmlType="submit"
                ariaLabel="Submit Form"
                isUppercase={false}
                className="px-5"
              >
                Save
              </Button>
              <Button
                htmlType="reset"
                ariaLabel="cancel edit"
                isUppercase={false}
                className="px-5"
                onClick={() => setIsModalOpen(true)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Layout.Content>
      </div>
    </>
  );
};
export default TemplateSetupComponent;
